import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import DOMPurify from 'isomorphic-dompurify';
import Media from '../../Generic/Carousel/CarouselMedia';

const Typography = dynamic(() => import('../../Generic/Typography'));

/**
 * JamieBanner Component
 * Unified component for rendering Jamie-style product banners (firstBanner, secondBanner, fourthBanner)
 * 
 * Data Flow:
 * - bannerData: Object containing banner content (title, bannerTitle, disclaimerText, etc.)
 * - bannerType: String indicating banner template type ('topBanner', 'simpleBanner', 'bottomBanner')
 * - bannerImage: Nested object with image URLs for desktop and smartphone
 * 
 * @component
 */
const JamieBanner = ({ bannerData, bannerType = 'topBanner' }) => {
  // Guard against missing data
  if (!bannerData) {
    return null;
  }

  // Detect if markdown starts with # (h1) or ## (h2) or __ (bold)
  const getHeadingComponent = (markdownText) => {
    if (markdownText.trim().startsWith('# ')) {
        return 'h1';
    } else if (markdownText.trim().startsWith('## ')) {
        return 'h2';
    } else if (markdownText.trim().startsWith('### ')) {
        return 'h3';
    } else if (markdownText.includes('__') || markdownText.includes('**')) {
        return 'strong';
    }
    return 'p'; // default
  };



  // Check if banner should render based on conditions
  const shouldRender = () => {
    const bannerImageUrl = bannerData?.bannerImage?.image?.desktopImage?.url;
    const bannerStyle = bannerData?.styles;
    
    if (bannerType === 'topBanner') {
      return bannerStyle === 'jamiepdpfirstbanner' && bannerImageUrl;
    } else if (bannerType === 'simpleBanner') {
      return bannerStyle === 'jamiepdpsecondbanner' && bannerImageUrl;
    } else if (bannerType === 'bottomBanner') {
      return bannerStyle === 'jamiepdpforthbanner' && bannerImageUrl;
    } else if (bannerType === 'ratingReviewBanner') {
      return bannerStyle === 'jamiepdpfifthbanner' && bannerImageUrl;
    }
    return false;
  };

  if (!shouldRender()) {
    return null;
  }

  // Render banner image - shared across all banner types
  const renderBannerImage = () => (
    <Media
      component="bannerPicture"
      srcset={bannerData?.bannerImage?.image?.desktopImage?.url}
      image={bannerData?.bannerImage?.image?.smartphoneImage?.url}
      altText={bannerData?.bannerImage?.image?.altText}
      className="w-full section-banner-img object-cover object-center"
    />
  );

  // Top Banner (jamiepdpfirstbanner)
  const renderTopBanner = () => (
    <div className={`min-h-min mb-10 component-top-banner relative clear-both pdp-banner ${bannerData.styles}`}>
      {renderBannerImage()}

      {/* COPY WRAPPER */}
      <div
        className="
          banner-copy
          absolute inset-0 flex flex-col justify-start md:justify-end gap-[12px] md:gap-6
          px-8 pb-10 lg:px-14 lg:pb-14 left-0 md:left-[125px] right-[7%] md:right-0
          top-[16%] md:top-0" tabIndex={0}
      >
         {bannerData?.title && (
          <div className="title-main">
          <h2 className="lg:block hidden title font-GigaSansBold m-0 border-0 uppercase leading-none tracking-[2%] text-black md:text-white text-left sm:text-[12px] smp:text-[16px] sml:text-[16px] mdp:text-[28px] md:text-[28px] lg:text-[28px] mb-4 md:mb-10" tabIndex={0}>
            {bannerData.title}
          </h2>
          </div>
        )}
        

        <hr className="hidden md:block mt-2 border-t border-white opacity-70 w-[300px]" />

        {/* Display bannerTitle */}
        {bannerData?.bannerTitle && (
          <div className="banner-title-main md:max-w-none ml-auto md:ml-0 lg:hidden" tabIndex={0}>
            <Typography
              component="div"
              className="mt-0 md:mt-20 border-0 uppercase tracking-[-0.02em]
              text-black md:text-white text-right md:text-left leading-[1.05]"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.bannerTitle || ''),
              }}
            />
          </div>
        )}

         {/* Display bannerTitle */}
        {bannerData?.bannerTitle && (
          <div className="banner-title-main md:max-w-none ml-auto md:ml-0 lg:block hidden" tabIndex={0}>
            <Typography
              component="div"
              className="font-GigaSansBold mt-0 md:mt-20 border-0 uppercase tracking-[2%]
              text-black md:text-white text-right md:text-left md:text-[28px] sm:text-[12px] smp:text-[28px] sml:text-[30px] mdp:text-[28px] md:text-[35px] lg:text-[35px] leading-[1.05]"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.bannerTitle || ''),
              }}
            />
          </div>
        )}
        
      </div>
      <div className="top-text-container absolute bottom-10 left-0 right-30 text-right block md:hidden" tabIndex={0}>
      {/* Display topText */}
        {bannerData?.topText && (
          <div className="top-text">
            <Typography
            component="p"
              className="w-fit float-right m-0 text-[12px] font-GigaSansRegular font-normal leading-[1.1] tracking-[-0.01em] text-black relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-px after:bg-[linear-gradient(90deg,#CC9E5CC9,#9D7C45F7)] lg:after:hidden pb-8"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.topText || ''),
              }}
            />
          </div>
        )}
      {/* Display disclaimerText */}
        {bannerData?.disclaimerText && (
          <div className="disclaimer-text relative clear-both" tabIndex={0}>
            <Typography
            component="p"
              className="pt-10 text-[8px] font-GigaSansBold font-normal leading-[1.1] tracking-[-0.01em] text-black"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.disclaimerText || ''),
              }}
            />
          </div>
        )}
        </div>
    </div>    
  );


  // Simple Banner (jamiepdpsecondbanner)
  const renderSimpleBanner = () => (
    <div className={`min-h-min mb-10 component-top-banner relative clear-both pdp-banner ${bannerData?.styles}`}>
      {renderBannerImage()}
      <div className="jamie-second absolute left-0 md:left-[200px] top-55 md:max-w-[500px] text-left right-50 md:right-0 md:top-[70px] lgxl:top-100 text-right md:text-left block float-right md:float-right lg:float-none">
        {/* Display title */}
        {bannerData?.title && (
          <div className="banner-title mb-6 w-[315px] float-right lg:float-none md:w-auto" tabIndex={0}>
            <Typography
              tabIndex="0"
              component="h2"
              className="m-0 font-GigaSansBold uppercase leading-none tracking-[1px] text-white text-[37px] md:text-[42px] lgxl:text-[57px]"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.title || ''),
              }}
            />
          </div>
        )}

        {/* Display bannerTitle */}
        {bannerData?.bannerTitle && (
          <div className="banner-title clear-both" tabIndex={0}>
            <Typography
              component="p"
              className="m-0 mt-[20px] text-19 md:text-[20px] lgxl:text-[25px] font-GigaSansRegular leading-[1.08] text-white"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.bannerTitle || ''),
              }}
            />
          </div>
        )}

        {/* Display disclaimerText */}
        {bannerData?.disclaimerText && (
          <Typography
            className="max-w-full md:max-w-[290px] text-[8px] font-GigaSansRegular font-normal uppercase leading-[1.08] tracking-[1px] text-white mt-[12px] md:mt-[20px] float-right md:float-none leading-normal"
            component="div"
            tabIndex={0}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bannerData?.disclaimerText),
            }}
          />
        )}
      </div>
    </div>
  );

  // Bottom Banner (jamiepdpforthbanner)
  const renderBottomBanner = () => (
    <div className={`min-h-min mb-10 component-top-banner relative clear-both pdp-banner ${bannerData.styles}`}>
      {renderBannerImage()}

      <div className="forth-container absolute inset-0 flex py-20 pr-0 pl-20 md:pl-[50px] lg:pl-[5%] md:pt-20 lg:pt-40">
        <div className="lg:w-1/2 w-full">
          {/* Title Section */}
          <div className="title-container mb-8 pt-[18px] lg:w-[40rem] flex flex-col block lg:hidden" tabIndex={0}>
            {/* Display title */}
            {bannerData?.title && (
              <Typography
                component="h2"
                className="title order-2 lg:order-1 m-0 text-15 lg:text-30 uppercase leading-[1.1] tracking-[-0.5px] mb-2 font-GigaSansRegular font-normal"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerData?.title || ''),
                }}
              />
            )}
            
            {/* Display bannerTitle */}
            {bannerData?.bannerTitle && (
              <Typography
                component="h2"
                className="banner-title order-1 lg:order-2 m-0 sm:text-[20px] smp:text-[20px] sml:text-[22px] lg:text-28 font-GigaSansExtraBold uppercase leading-[1.19] tracking-[1%] text-black font-extrabold"
                tabIndex={0}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerData?.bannerTitle || ''),
                }}
              />
            )}
          </div>

           {/* Display disclaimerText */}
            {bannerData?.disclaimerText && (
              <div className="hidden lg:block indicado-txt" tabIndex={0}>
              <Typography
                component="h2"
                className="order-1 lg:order-2 m-0 text-22 md:text-[20px] lgxl:text-28 font-GigaSansExtraBold uppercase leading-[1.19] tracking-[1%] text-black font-extrabold"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerData?.disclaimerText || ''),
                }}
              />
              </div>
            )}

          {/* Specifications Section */}
          <div className="forth-bottom absolute bottom-[34px] md:bottom-[14px] lg:bottom-20 flex flex-col gap-10 ml-[51%] md:ml-0 lg:ml-0 font-GigaSansRegular font-normal text-[clamp(14px,2vw,28px)]">
            {bannerData?.topText && (
              <Typography
                component="p"
                tabIndex={0}
                className="desen-text max-w-[120px] md:max-w-none m-0 text-12 md:text-14 font-normal uppercase text-black mb-4 font-GigaSansRegular"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerData?.topText || ''),
                }}
              />
            )}

            {/* Damage Level */}
            <div className="level-one mb-2 md:flex lg:flex items-center lg:justify-between lg:w-[250px] md:justify-between md:w-[250px] font-GigaSansRegular font-normal">
              <p className="m-0 text-[11px] font-normal leading-none tracking-[2%] text-black" tabIndex={0}>
                {bannerData?.hairTypeLevel1}
              </p>
              <div className="flex gap-2" role="img" aria-label="Nível 3 de dano" tabIndex={0}>
                <div className="dots w-[26px] h-[7px] md:w-[32px] md:h-[11px] rounded-[5px]" style={{ background: 'linear-gradient(90deg, rgb(112, 82, 48) 0%, rgb(155, 115, 64) 100%)' }} aria-hidden="true" />
                <div className="dots w-[26px] h-[7px] md:w-[32px] md:h-[11px] rounded-[5px]" style={{ background: 'linear-gradient(90deg, rgb(112, 82, 48) 0%, rgb(155, 115, 64) 100%)' }} aria-hidden="true" />
                <div className="dots w-[26px] h-[7px] md:w-[32px] md:h-[11px] rounded-[5px]" style={{ background: 'linear-gradient(90deg, rgb(112, 82, 48) 0%, rgb(155, 115, 64) 100%)' }} aria-hidden="true" />
              </div>
            </div>

            {/* Oiliness Level */}
            <div className="level-two mb-2 md:flex lg:flex items-center lg:justify-between lg:w-[250px] md:justify-between md:w-[250px] font-GigaSansRegular font-normal">
              <p className="m-0 text-[11px] font-normal leading-none tracking-[2%] text-black" tabIndex={0}>
                {bannerData?.hairTypeLevel2}
              </p>
             <div className="flex gap-2" role="img" aria-label="Nível 1 de oleosidade" tabIndex={0}>
                <div className="dots w-[26px] h-[7px] md:w-[32px] md:h-[11px] rounded-[5px]" style={{ background: 'linear-gradient(90deg, rgb(112, 82, 48) 0%, rgb(155, 115, 64) 100%)' }} aria-hidden="true" />
                <div className="dots w-[26px] h-[7px] md:w-[32px] md:h-[11px] rounded-[5px] border border-amber-900" aria-hidden="true" />
                <div className="dots w-[26px] h-[7px] md:w-[32px] md:h-[11px] rounded-[5px] border border-amber-900" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Rating Review Banner (jamiepdpfithbanner)
  const renderRatingReviewBanner = () => (
    <div className={`min-h-min mb-10 component-top-banner relative clear-both pdp-banner ${bannerData.styles}`}>
      {renderBannerImage()}
      <div className="absolute inset-0 flex flex-col md:flex-row items-stretch justify-between p-6 md:p-12 font-sans overflow-hidden select-none">
        {/* Left Section */}
        <div className="ordered-items w-fit md:w-auto flex flex-col justify-start gap-4 text-black max-w-xs ml-8 md:ml-16 order-3 md:order-1 pb-6 md:pb-0 lg:pl-[2.8%] md:pt-10 lg:pt-30">
          {bannerData?.topText && (
            <Typography
              component="div"
              tabIndex={0}
              className="font-GigaSansRegular font-normal numbered-items"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(bannerData?.topText || ''),
              }}
            />
          )}
        </div>

        {/* Middle Spacer */}
        <div className="h-64 md:h-auto md:w-2/4 pointer-events-none order-2" />

        {/* Right Section */}
        <div className="right-section w-full md:w-auto flex flex-col justify-start text-right text-black gap-3 order-1 md:order-3 pr-20 pt-20 md:pr-40 md:pt-40">
          {/* Display bannerTitle */}
          {bannerData?.bannerTitle && (
            <div className="banner-title-main mb-10 right-text" tabIndex={0}>
              <Typography
                component="div"
                className="text-black"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(bannerData?.bannerTitle || ''),
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render based on banner type
  const renderSwitch = () => {
    switch (bannerType) {
      case 'topBanner':
        return renderTopBanner();
      case 'simpleBanner':
        return renderSimpleBanner();
      case 'bottomBanner':
        return renderBottomBanner();
      case 'ratingReviewBanner':
        return renderRatingReviewBanner();
      default:
        return null;
    }
  };

  return renderSwitch();
};

JamieBanner.propTypes = {
  /** Banner data object containing all content and metadata */
  bannerData: PropTypes.shape({
    styles: PropTypes.string,
    bannerImage: PropTypes.shape({
      image: PropTypes.shape({
        desktopImage: PropTypes.shape({
          url: PropTypes.string,
        }),
        smartphoneImage: PropTypes.shape({
          url: PropTypes.string,
        }),
        altText: PropTypes.string,
      }),
    }),
    title: PropTypes.string,
    bannerTitle: PropTypes.string,
    disclaimerText: PropTypes.string,
    topText: PropTypes.string,
    percentageText: PropTypes.string,
    discoverNewText: PropTypes.string,
    hairTypeLevel1: PropTypes.string,
  }),
  /** Banner template type: 'topBanner' | 'simpleBanner' | 'bottomBanner' | 'ratingReviewBanner' */
  bannerType: PropTypes.oneOf(['topBanner', 'simpleBanner', 'bottomBanner', 'ratingReviewBanner']),
};

JamieBanner.defaultProps = {
  bannerData: null,
  bannerType: 'topBanner',
};

export default JamieBanner;